import React, { useState, useEffect } from 'react'
import {X, Search} from 'lucide-react'
import { useInsumos } from '../hooks/useInsumos'
import { usePlanejamentoObras } from '../hooks/usePlanejamentoObras'
import { useFuncionarios } from '../hooks/useFuncionarios'

interface RequisicaoFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  initialData?: any
  title: string
}

const RequisicaoForm: React.FC<RequisicaoFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title
}) => {
  const { insumos } = useInsumos()
  const { obras } = usePlanejamentoObras()
  const { funcionarios } = useFuncionarios()
  
  const [formData, setFormData] = useState({
    insumoId: '',
    quantidade: 0,
    responsavelRequisicao: '',
    responsavelAprovacao: '',
    obraId: '',
    etapaId: '',
    observacoes: '',
    urgente: false
  })

  // Estados para o dropdown aprimorado de insumos
  const [insumoSearchTerm, setInsumoSearchTerm] = useState('')
  const [showInsumoDropdown, setShowInsumoDropdown] = useState(false)
  const [isInsumoSearchMode, setIsInsumoSearchMode] = useState(false)

  // Initialize form with existing data
  useEffect(() => {
    if (initialData) {
      setFormData({
        insumoId: initialData.insumoId || '',
        quantidade: initialData.quantidade || 0,
        responsavelRequisicao: initialData.responsavelRequisicao || '',
        responsavelAprovacao: initialData.responsavelAprovacao || '',
        obraId: initialData.obraId || '',
        etapaId: initialData.etapaId || '',
        observacoes: initialData.observacoes || '',
        urgente: initialData.urgente || false
      })
    }
  }, [initialData])

  // Get selected insumo
  const selectedInsumo = insumos.find(i => i._id === formData.insumoId)

  // Get selected obra
  const selectedObra = obras.find(o => o._id === formData.obraId)

  // Get etapas from selected obra
  const etapasDisponiveis = selectedObra?.etapas || []

  // Get funcionários ativos para responsáveis
  const funcionariosAtivos = funcionarios.filter(f => f.status === 'ativo')

  // Get funcionários com perfis de aprovação (gestores, coordenadores, etc.)
  const funcionariosAprovacao = funcionariosAtivos.filter(f => 
    ['Gestor', 'Coordenador', 'Engenheiro', 'Gerente'].some(cargo => 
      f.cargo.toLowerCase().includes(cargo.toLowerCase()) || 
      f.funcao.toLowerCase().includes(cargo.toLowerCase())
    )
  )

  // Filtrar insumos para o dropdown
  const filteredInsumos = insumos.filter(insumo => 
    insumo.ativo && 
    insumo.nome.toLowerCase().includes(insumoSearchTerm.toLowerCase())
  )

  // Função para selecionar insumo
  const selectInsumo = (insumo: any) => {
    setFormData(prev => ({ ...prev, insumoId: insumo._id }))
    setInsumoSearchTerm('')
    setShowInsumoDropdown(false)
    setIsInsumoSearchMode(false)
  }

  // Função para ativar modo de pesquisa
  const activateInsumoSearch = () => {
    setIsInsumoSearchMode(true)
    setShowInsumoDropdown(true)
    setInsumoSearchTerm('')
  }

  // 🔥 FUNÇÃO AUXILIAR: Formatar preço com segurança
  const formatPreco = (preco: any): string => {
    const precoNum = Number(preco)
    if (isNaN(precoNum) || precoNum === null || precoNum === undefined) {
      return '0,00'
    }
    return precoNum.toFixed(2).replace('.', ',')
  }

  // Update centro de custo when obra changes
  useEffect(() => {
    if (selectedObra) {
      setFormData(prev => ({
        ...prev,
        etapaId: '' // Reset etapa when obra changes
      }))
    }
  }, [selectedObra])

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedInsumo) {
      alert('Selecione um insumo')
      return
    }

    if (!formData.obraId) {
      alert('Selecione uma obra')
      return
    }

    if (!formData.responsavelRequisicao) {
      alert('Selecione o responsável pela requisição')
      return
    }

    if (!formData.responsavelAprovacao) {
      alert('Selecione o responsável pela aprovação')
      return
    }

    const submitData = {
      ...formData,
      nomeInsumo: selectedInsumo.nome,
      unidadeMedida: selectedInsumo.unidadeMedida,
      centroCusto: selectedObra?.numeroContrato || selectedObra?.nomeObra || ''
    }

    await onSubmit(submitData)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informações da Requisição */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Informações da Requisição</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Dropdown aprimorado de Insumo */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Insumo *
                </label>
                <div className="relative">
                  {isInsumoSearchMode ? (
                    <div className="relative">
                      <input
                        type="text"
                        value={insumoSearchTerm}
                        onChange={(e) => setInsumoSearchTerm(e.target.value)}
                        onFocus={() => setShowInsumoDropdown(true)}
                        onBlur={() => {
                          setTimeout(() => {
                            setShowInsumoDropdown(false)
                            setIsInsumoSearchMode(false)
                          }, 200)
                        }}
                        placeholder="Digite para pesquisar insumos..."
                        className="w-full p-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <Search 
                        size={20} 
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                    </div>
                  ) : (
                    <div className="flex">
                      <select
                        value={formData.insumoId}
                        onChange={(e) => setFormData(prev => ({ ...prev, insumoId: e.target.value }))}
                        required
                        className="w-full p-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Selecionar insumo...</option>
                        {insumos.filter(i => i.ativo).map(insumo => (
                          <option key={insumo._id} value={insumo._id}>
                            {insumo.nome} - {insumo.unidadeMedida}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={activateInsumoSearch}
                        className="px-3 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100 focus:ring-2 focus:ring-blue-500"
                        title="Pesquisar insumos"
                      >
                        <Search size={16} className="text-gray-600" />
                      </button>
                    </div>
                  )}

                  {/* Dropdown de resultados da pesquisa */}
                  {showInsumoDropdown && isInsumoSearchMode && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredInsumos.length > 0 ? (
                        filteredInsumos.map(insumo => (
                          <div
                            key={insumo._id}
                            onClick={() => selectInsumo(insumo)}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{insumo.nome}</div>
                            <div className="text-sm text-gray-500">
                              {insumo.tipo} • {insumo.unidadeMedida} • R$ {formatPreco(insumo.precoUnitario)}
                              {insumo.controlaEstoque && (
                                <span className="ml-2 text-blue-600">
                                  Estoque: {insumo.quantidadeEstoque || 0}
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500 text-center">
                          Nenhum insumo encontrado
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.quantidade}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantidade: Number(e.target.value) }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável pela Requisição *
                </label>
                <select
                  value={formData.responsavelRequisicao}
                  onChange={(e) => setFormData(prev => ({ ...prev, responsavelRequisicao: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecionar funcionário...</option>
                  {funcionariosAtivos.map(funcionario => (
                    <option key={funcionario._id} value={funcionario.nome}>
                      {funcionario.nome} - {funcionario.cargo}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável pela Aprovação *
                </label>
                <select
                  value={formData.responsavelAprovacao}
                  onChange={(e) => setFormData(prev => ({ ...prev, responsavelAprovacao: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecionar responsável...</option>
                  {funcionariosAprovacao.length > 0 ? (
                    funcionariosAprovacao.map(funcionario => (
                      <option key={funcionario._id} value={funcionario.nome}>
                        {funcionario.nome} - {funcionario.cargo}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Nenhum funcionário com perfil de aprovação encontrado</option>
                  )}
                  {/* Fallback para todos os funcionários ativos */}
                  <optgroup label="Outros funcionários">
                    {funcionariosAtivos
                      .filter(f => !funcionariosAprovacao.some(fa => fa._id === f._id))
                      .map(funcionario => (
                        <option key={funcionario._id} value={funcionario.nome}>
                          {funcionario.nome} - {funcionario.cargo}
                        </option>
                      ))
                    }
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Obra *
                </label>
                <select
                  value={formData.obraId}
                  onChange={(e) => setFormData(prev => ({ ...prev, obraId: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecionar obra...</option>
                  {obras.filter(o => ['planejamento', 'em_andamento'].includes(o.status)).map(obra => (
                    <option key={obra._id} value={obra._id}>
                      {obra.nomeObra} - {obra.numeroContrato}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Etapa
                </label>
                <select
                  value={formData.etapaId}
                  onChange={(e) => setFormData(prev => ({ ...prev, etapaId: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={!selectedObra}
                >
                  <option value="">Selecionar etapa...</option>
                  {etapasDisponiveis.map((etapa: any) => (
                    <option key={etapa.id} value={etapa.id}>
                      {etapa.numero} - {etapa.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações
              </label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Informações adicionais sobre a requisição..."
              />
            </div>

            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.urgente}
                  onChange={(e) => setFormData(prev => ({ ...prev, urgente: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Requisição Urgente</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {initialData ? 'Atualizar' : 'Criar'} Requisição
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default RequisicaoForm
